"use client"

import { useState, useEffect, useCallback, useRef } from "react"
import { Block, Theme, defaultTheme } from "@/types"
import { MessageBlock } from "./preview-blocks/MessageBlock"
import { MultipleChoiceBlock } from "./preview-blocks/MultipleChoiceBlock"
import { ShortAnswerBlock } from "./preview-blocks/ShortAnswerBlock"
import { CTABlock } from "./preview-blocks/CTABlock"
import { RecommendationBlock } from "./preview-blocks/RecommendationBlock"
import { EmailBlock } from "./preview-blocks/EmailBlock"
import { PhoneBlock } from "./preview-blocks/PhoneBlock"
import { Smartphone, Monitor } from "lucide-react"

interface StepByStepPreviewProps {
  blocks: Block[]
  mode: "desktop" | "mobile"
  onModeChange: (mode: "desktop" | "mobile") => void
  onStepView?: (stepId?: string) => void
  onStepComplete?: (stepId?: string, metadata?: any) => void
  onCtaClick?: (stepId?: string, metadata?: any) => void
  onAnswerEvent?: (block: Block, answer: any) => void
  isShopifyProxy?: boolean
  shopDomain?: string | null
  projectId?: string
  showHeader?: boolean
  theme?: Theme | null
  selectedBlockId?: string | null // New prop to track selected block from editor
  isEmbedded?: boolean // New prop to handle embed-specific layout
  // Shopify embed-specific cart functions
  getProduct?: (handle: string) => Promise<any>
  addToCart?: (variantId: number, quantity?: number, properties?: Record<string, string>) => Promise<any>
  onAddedToCart?: () => void
}

export function StepByStepPreview({ 
  blocks, 
  mode, 
  onModeChange, 
  onStepView, 
  onStepComplete, 
  onCtaClick,
  onAnswerEvent,
  isShopifyProxy = false,
  shopDomain = null,
  projectId,
  showHeader = true,
  theme = null,
  selectedBlockId = null,
  isEmbedded = false,
  getProduct,
  addToCart,
  onAddedToCart
}: StepByStepPreviewProps) {
  const sortedBlocks = [...blocks].sort((a, b) => a.order - b.order)
  const [currentStep, setCurrentStep] = useState(0)
  const [isTransitioning, setIsTransitioning] = useState(false)
  const [answers, setAnswers] = useState<Record<string, any>>({})
  const [hasTrackedFirstView, setHasTrackedFirstView] = useState(false)
  const prevSelectedBlockIdRef = useRef<string | null>(null)
  
  // Use provided theme or fallback to default
  const activeTheme = theme || defaultTheme

  // Reset to first step when blocks change
  useEffect(() => {
    setCurrentStep(0)
    setAnswers({})
    setHasTrackedFirstView(false)
    // Reset the ref when blocks change
    prevSelectedBlockIdRef.current = null
  }, [blocks])

  // Track initial step view separately to ensure it fires correctly
  useEffect(() => {
    if (!hasTrackedFirstView && sortedBlocks.length > 0 && onStepView) {
      onStepView(sortedBlocks[0].id)
      setHasTrackedFirstView(true)
    }
  }, [sortedBlocks, hasTrackedFirstView, onStepView])

  // Update current step only when selectedBlockId actually changes (for editor sync)
  useEffect(() => {
    // Only update if selectedBlockId actually changed and is not null
    if (selectedBlockId && sortedBlocks.length > 0 && prevSelectedBlockIdRef.current !== selectedBlockId) {
      const blockIndex = sortedBlocks.findIndex(block => block.id === selectedBlockId)
      if (blockIndex !== -1) {
        setCurrentStep(blockIndex)
      }
    }
    // Update the ref to track the current selectedBlockId
    prevSelectedBlockIdRef.current = selectedBlockId
  }, [selectedBlockId, sortedBlocks])

  const currentBlock = sortedBlocks[currentStep]
  const totalSteps = sortedBlocks.length
  const progress = totalSteps > 0 ? ((currentStep + 1) / totalSteps) * 100 : 0

  // Helper to determine if a block is a question type
  const isQuestionBlock = (type: string) => {
    return ['MULTIPLE_CHOICE', 'SHORT_ANSWER', 'EMAIL', 'PHONE'].includes(type)
  }

  const handleNext = useCallback(() => {
    // Track the FINAL answer for this step (if it's a question)
    if (currentBlock && isQuestionBlock(currentBlock.type)) {
      const finalAnswer = answers[currentBlock.id]
      if (finalAnswer !== undefined && onAnswerEvent) {
        onAnswerEvent(currentBlock, finalAnswer)
      }
    }
    
    // Track step completion (fires for ALL steps including the last one)
    if (currentBlock) {
      onStepComplete?.(currentBlock.id)
    }
    
    // Mark current block as completed if it's a MESSAGE block
    if (currentBlock?.type === "MESSAGE" && !answers[currentBlock.id]) {
      setAnswers(prev => ({ ...prev, [currentBlock.id]: "completed" }))
    }
    
    // Only navigate if not on the last step
    if (currentStep < totalSteps - 1) {
      setIsTransitioning(true)
      setTimeout(() => {
        setCurrentStep(currentStep + 1)
        setIsTransitioning(false)
        
        // Track step view for next block
        const nextBlock = sortedBlocks[currentStep + 1]
        if (nextBlock) {
          onStepView?.(nextBlock.id)
        }
      }, 150)
    } else {
      // On last step - mark as completed and optionally show completion message
      // For now, just ensure the tracking fired (which it did above)
    }
  }, [currentBlock, currentStep, totalSteps, answers, sortedBlocks, onAnswerEvent, onStepComplete, onStepView])

  const handleAnswer = useCallback((blockId: string, answer: any) => {
    setAnswers(prev => ({ ...prev, [blockId]: answer }))
    // Don't track here - we'll track the final answer in handleNext instead
  }, [])

  const handleAutoAdvance = useCallback(() => {
    handleNext()
  }, [handleNext])

  const handleFinalStepCtaClick = (metadata?: any) => {
    if (currentStep === totalSteps - 1 && currentBlock?.id) {
      // signal completion with keepalive so it survives navigation
      // We'll pass a metadata flag the parent can ignore; the parent just needs to use keepalive:true
      (onStepComplete as any)?.(currentBlock.id, { transport: 'beacon' })
    }
    // Then delegate to the original CTA click
    onCtaClick?.(currentBlock?.id, metadata)
  }

  const renderBlock = (block: Block) => {
    const blockProps = {
      content: block.content as any,
      onAnswer: (answer: any) => handleAnswer(block.id, answer),
      answer: answers[block.id],
      allAnswers: answers,
      allBlocks: sortedBlocks,
      theme: activeTheme,
      previewMode: showHeader ? mode : undefined // Only pass mode in builder preview
    }

    switch (block.type) {
      case "MESSAGE":
        const messageContent = block.content as any
        return <MessageBlock 
          key={block.id} 
          {...blockProps}
          onAutoAdvance={messageContent.autoAdvance ? handleAutoAdvance : undefined}
        />
      case "MULTIPLE_CHOICE":
        return <MultipleChoiceBlock key={block.id} {...blockProps} onAutoAdvance={handleAutoAdvance} />
      case "SHORT_ANSWER":
        return <ShortAnswerBlock key={block.id} {...blockProps} />
      case "EMAIL":
        return <EmailBlock key={block.id} {...blockProps} />
      case "PHONE":
        return <PhoneBlock key={block.id} {...blockProps} />
      case "CTA":
        const ctaContent = block.content as any
        // Check if any button has nextStep mode (either from buttons array or legacy mode field)
        const hasNextStepButton = ctaContent.buttons?.some((btn: any) => btn.mode === 'nextStep') 
          || ctaContent.mode === 'nextStep'
        return <CTABlock 
          key={block.id} 
          {...blockProps} 
          isShopifyProxy={isShopifyProxy}
          shopDomain={shopDomain}
          projectId={projectId}
          onCtaClick={(stepId, metadata) => handleFinalStepCtaClick(metadata)}
          onAutoAdvance={hasNextStepButton ? handleAutoAdvance : undefined}
          onAnswerEvent={(answer) => onAnswerEvent?.(block, answer)}
        />
      case "RECOMMENDATION":
        return <RecommendationBlock 
          key={block.id} 
          {...blockProps} 
          isShopifyProxy={isShopifyProxy}
          shopDomain={shopDomain}
          projectId={projectId}
          mode={mode}
          onCtaClick={(stepId, metadata) => handleFinalStepCtaClick(metadata)}
          getProduct={getProduct}
          addToCart={addToCart}
          onAddedToCart={onAddedToCart}
        />
      default:
        return null
    }
  }

  const canProceed = () => {
    if (!currentBlock) return false
    
    const answer = answers[currentBlock.id]
    
    // Must have an answer
    if (answer === undefined) return false
    
    // For MULTIPLE_CHOICE: array must have at least one item selected
    if (currentBlock.type === "MULTIPLE_CHOICE") {
      return Array.isArray(answer) ? answer.length > 0 : true
    }
    
    // For SHORT_ANSWER: string must not be empty (after trimming whitespace)
    if (currentBlock.type === "SHORT_ANSWER") {
      return typeof answer === 'string' ? answer.trim().length > 0 : true
    }
    
    // For EMAIL and PHONE: string must not be empty (after trimming whitespace)
    if (currentBlock.type === "EMAIL" || currentBlock.type === "PHONE") {
      return typeof answer === 'string' ? answer.trim().length > 0 : true
    }
    
    // For MESSAGE blocks and other types, just need answer to exist
    return true
  }

  const shouldShowNavigation = () => {
    if (!currentBlock) return false
    
    // Hide navigation for CTA blocks - they handle their own actions
    if (currentBlock.type === "CTA") {
      return false
    }
    
    // Hide navigation on the final step entirely (for RECOMMENDATION blocks)
    if (currentStep === totalSteps - 1) {
      return false
    }
    
    // Hide navigation for single-choice multiple choice (auto-advances)
    if (currentBlock.type === "MULTIPLE_CHOICE") {
      const content = currentBlock.content as any
      // Only show navigation for multi-select (single-select auto-advances)
      return content.allowMultiple === true
    }
    
    // For SHORT_ANSWER, EMAIL, PHONE: always show navigation (will be disabled until answered)
    if (['SHORT_ANSWER', 'EMAIL', 'PHONE'].includes(currentBlock.type)) {
      return true
    }
    
    // For MESSAGE blocks, check if auto-advance is enabled
    if (currentBlock.type === "MESSAGE") {
      const content = currentBlock.content as any
      // Hide navigation if auto-advancing
      if (content.autoAdvance) {
        return false
      }
      // Otherwise, always show navigation (will be disabled until animation completes)
      return true
    }
    
    // Show navigation for other block types immediately
    return true
  }

  return (
    <div className="flex flex-col">
      {/* Top Progress Indicator - only show in live mode and when enabled in theme */}
      {!showHeader && totalSteps > 0 && activeTheme.showProgressIndicator && (
        <div className="w-full h-1 bg-gray-200 relative">
          <div 
            className="h-full transition-all duration-300 ease-out"
            style={{ 
              width: `${progress}%`,
              backgroundColor: activeTheme.colors.primary 
            }}
          />
        </div>
      )}

      {/* Preview Header - only show when showHeader is true */}
      {showHeader && (
        <div className="p-4 bg-white shadow-sm">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              {totalSteps > 0 && (
                <div className="flex items-center space-x-2 text-sm text-gray-600">
                  <span>Step {currentStep + 1} of {totalSteps}</span>
                  <div className="w-32 bg-gray-200 rounded-full h-2">
                    <div 
                      className="h-2 rounded-full transition-all duration-300 ease-out"
                      style={{ 
                        width: `${progress}%`,
                        backgroundColor: activeTheme.colors.primary 
                      }}
                    />
                  </div>
                </div>
              )}
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => onModeChange("desktop")}
                className={`p-2 rounded-md transition-colors ${
                  mode === "desktop"
                    ? "bg-indigo-100 text-indigo-600"
                    : "text-gray-500 hover:bg-gray-100"
                }`}
              >
                <Monitor className="w-5 h-5" />
              </button>
              <button
                onClick={() => onModeChange("mobile")}
                className={`p-2 rounded-md transition-colors ${
                  mode === "mobile"
                    ? "bg-indigo-100 text-indigo-600"
                    : "text-gray-500 hover:bg-gray-100"
                }`}
              >
                <Smartphone className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Preview Content */}
      <div className={`flex-1 ${showHeader ? 'bg-gray-100 p-2 overflow-y-auto' : 'p-0'}`}>
        <div
          className={`${
            showHeader 
              ? `mx-auto shadow-lg min-h-full flex flex-col justify-center ${mode === "mobile" ? "max-w-sm" : "max-w-full"}`
              : 'flex flex-col'
          }`}
          style={{
            backgroundColor: activeTheme.colors.background,
            fontFamily: activeTheme.fonts.body,
            color: activeTheme.colors.text
          }}
        >
          {totalSteps === 0 ? (
            <div className="flex-1 flex items-center justify-center p-8 text-center text-gray-500">
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  No blocks yet
                </h3>
                <p className="text-gray-600">
                  Add blocks from the editor to see them in the preview
                </p>
              </div>
            </div>
          ) : (
            <>
              {/* Check if current block has an image for split layout */}
              {currentBlock && (currentBlock.content as any)?.image?.src ? (
                /* Split Layout: Content left (desktop) / top (mobile), Image right (desktop) / bottom (mobile) */
                <div className={`flex ${mode === "mobile" ? "flex-col" : "flex-col md:flex-row"} ${mode === "mobile" ? "gap-2" : "gap-2 md:gap-6"} ${mode === "mobile" ? "min-h-[400px]" : "min-h-[400px] md:min-h-[500px]"} ${!showHeader && !isEmbedded ? 'pb-24' : ''}`}>
                  {/* Content + Navigation - top on mobile, left on desktop */}
                  <div className={`order-1 ${mode === "mobile" ? "w-full" : "md:w-[40%]"} flex flex-col justify-center`}>
                    <div className="pt-8 px-10 pb-4">
                      <div className={`transition-all duration-300 ease-in-out ${
                        isTransitioning 
                          ? "opacity-0 transform translate-x-4" 
                          : "opacity-100 transform translate-x-0"
                      }`}>
                        {renderBlock(currentBlock)}
                      </div>
                    </div>
                    
                    {/* Navigation within content column for alignment */}
                    {currentBlock?.type !== "RECOMMENDATION" && shouldShowNavigation() && (
                      <div className="px-4 pb-4">
                        <div className="flex justify-center transition-all duration-300 ease-in-out animate-fade-in">
                          <button
                            onClick={handleNext}
                            disabled={!canProceed()}
                            className={`px-8 py-2 rounded-full font-medium text-lg transition-all ${
                              !canProceed()
                                ? "cursor-not-allowed opacity-40"
                                : "shadow-lg opacity-100"
                            }`}
                            style={{
                              backgroundColor: currentStep === totalSteps - 1
                                ? '#059669' // green-600
                                : activeTheme.colors.primary,
                              color: activeTheme.colors.buttonText
                            }}
                          >
                            {currentStep === totalSteps - 1 ? "Complete" : "Continue"}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {/* Image - bottom on mobile, right on desktop */}
                  <div className={`order-2 ${mode === "mobile" ? "w-full" : "md:w-[60%]"} bg-gray-50 overflow-hidden`}>
                    <img 
                      src={(currentBlock.content as any).image.src} 
                      alt={(currentBlock.content as any).image.caption || ''}
                      className="w-full h-full object-cover"
                      style={{
                        WebkitMaskImage: (currentBlock.content as any).image.fadeEdge 
                          ? 'linear-gradient(to right, transparent 0%, black 5%, black 100%)' 
                          : undefined,
                        maskImage: (currentBlock.content as any).image.fadeEdge 
                          ? 'linear-gradient(to right, transparent 0%, black 5%, black 100%)' 
                          : undefined,
                      }}
                    />
                  </div>
                </div>
              ) : (
                /* Normal Layout: No image, centered content */
                <>
                  <div className={`min-h-[400px] md:min-h-[500px] flex flex-col ${!showHeader && !isEmbedded ? 'pb-24' : ''}`}>
                    {/* Current Step Content - grows to take available space and centers vertically */}
                    <div className="flex-grow flex items-center w-full py-8 px-10">
                      <div 
                        className={`w-full transition-all duration-300 ease-in-out ${
                          isTransitioning 
                            ? "opacity-0 transform translate-x-4" 
                            : "opacity-100 transform translate-x-0"
                        }`}
                      >
                        {currentBlock && renderBlock(currentBlock)}
                      </div>
                    </div>

                    {/* Navigation - inside min-height container, flows after content */}
                    {/* Hide entire navigation container for RECOMMENDATION blocks */}
                    {currentBlock?.type !== "RECOMMENDATION" && shouldShowNavigation() && (
                      <div className="px-4 pb-16">
                        <div className="flex justify-center transition-all duration-300 ease-in-out animate-fade-in">
                          <button
                            onClick={handleNext}
                            disabled={!canProceed()}
                            className={`px-8 py-2 rounded-full font-medium text-lg transition-all ${
                              !canProceed()
                                ? "cursor-not-allowed opacity-40"
                                : "shadow-lg opacity-100"
                            }`}
                            style={{
                              backgroundColor: currentStep === totalSteps - 1
                                ? '#059669' // green-600
                                : activeTheme.colors.primary,
                              color: activeTheme.colors.buttonText
                            }}
                          >
                            {currentStep === totalSteps - 1 ? "Complete" : "Continue"}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {/* Bottom spacer for embedded mode to ensure space below the quiz */}
                  {isEmbedded && (
                    <div style={{ height: '4rem' }} aria-hidden="true" />
                  )}
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  )
}
